/**
 * Keep this file in sync with benchling/data_frames/column_types/validation/validate_column_type.py
 */
import {assert} from 'Asserts';
import {DataFrameColumnType} from 'data-frames/DataFrameTypes';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';

import ColumnTypeValidationException from './ColumnTypeValidationException';

export enum ColumnTypeValidationFlag {
  ALLOWED = 'ALLOWED',
  REQUIRED = 'REQUIRED',
  NOT_ALLOWED = 'NOT_ALLOWED',
}

export interface ColumnTypeValidationOptions {
  /** If not specified, all leaf types will be allowed and validateColumnType will return LeafType.JSON */
  allowedLeafTypes?: ReadonlyArray<LeafType>;
  listType?: ColumnTypeValidationFlag;
  nullableType?: ColumnTypeValidationFlag;
  /** @deprecated */
  maybeTextType?: ColumnTypeValidationFlag;
  /**
   * Whether or not to allow child types defined in LEAF_TYPE_CHILD_TYPES
   *
   * @deprecated
   */
  strictLeafType?: boolean;
}

/**
 * This tree defines the parent-child relationships among leaf types,
 * where any child leaf type can be used as its parent leaf type without value conversion.
 *
 * This guarantee only applies to the serialized form. For example, a `DATE` column can be used when a `TEXT`
 * column is expected, but only if the values are used as as strings and not deserialized to `Date` objects.
 *
 * The serialization forms are defined by `leaf_type_validators.py`. See `leaf_type_validators_test.py` for
 * example serialized values.
 *
 * This should not match the inheritance of classes in `leaf_types.py`, which reflects what leaf types are
 * conceptually related, rather than which column types can be used polymorphically.
 *
 * A unit test enforces this tree contains all leaf types.
 *
 * `LeafType.INTEGER` is not a child of `LeafType.DECIMAL` because `LeafType.INTEGER` is serialized as
 * `number` while `LeafType.DECIMAL` is serialized as `string`.
 */
export const LEAF_TYPE_CHILD_TYPES: {[key in LeafType]?: Array<LeafType>} = {
  [LeafType.JSON]: [LeafType.BOOLEAN, LeafType.INTEGER, LeafType.TEXT],
  [LeafType.TEXT]: [LeafType.BENCHLING_OBJECT, LeafType.DATETIME, LeafType.DECIMAL],
  [LeafType.DATETIME]: [LeafType.DATE],
  [LeafType.BENCHLING_OBJECT]: [
    LeafType.BLOB,
    LeafType.BATCH,
    LeafType.BIOENTITY,
    LeafType.CHART,
    LeafType.DATA_FRAME,
    LeafType.DATASET,
    LeafType.DROPDOWN_OPTION,
    LeafType.ENTRY,
    LeafType.FILE,
    LeafType.PROCESS,
    LeafType.PROCESS_TASK,
    LeafType.PROCESS_OUTPUT,
    LeafType.PROTOCOL,
    LeafType.RESULT,
    LeafType.REQUEST,
    LeafType.RUN,
    LeafType.SCRATCH_FILE,
    LeafType.STORABLE,
    LeafType.STUDY,
    LeafType.TEAM,
    LeafType.TEST_ORDER,
    LeafType.USER,
    LeafType.WORKSHEET,
  ],
  [LeafType.BIOENTITY]: [
    LeafType.BASIC_FOLDER_ITEM,
    LeafType.MIXTURE,
    LeafType.MOLECULE,
    LeafType.PROTEIN,
    LeafType.SEQUENCE,
  ],
  [LeafType.STORABLE]: [LeafType.BOX, LeafType.CONTAINER, LeafType.LOCATION, LeafType.PLATE],
};

/**
 * Returns the leaf type from `allowedLeafTypes` that matches `leafTypeToValidate`.
 *
 * This function first checks for an exact match (`leafTypeToValidate` is listed in `allowedLeafTypes`) then,
 * if there is no exact match, checks if `leafTypeToValidate` is a child of any type in `allowedLeafType`.
 *
 * If `leafTypeToValidate` matches a type in `allowedLeafType`, either exactly or as a child, the matching
 * type from `allowedLeafType` will be returned. Else, null is returned.
 */
function findAllowedLeafType(
  leafTypeToValidate: LeafType,
  allowedLeafTypes: ReadonlyArray<LeafType>,
  strictLeafType: boolean
): LeafType | null {
  // Check if the column's type is an exact match for any of the allowed leaf types.
  for (const allowedLeafType of allowedLeafTypes) {
    if (allowedLeafType === leafTypeToValidate) {
      return allowedLeafType;
    }
  }

  // Recursively check if the column's type is a child of any of the allowed leaf types. Return the first match.
  // Run this after the first loop so an exact match is always returned before a parent match.
  if (!strictLeafType) {
    for (const allowedLeafType of allowedLeafTypes) {
      if (
        findAllowedLeafType(leafTypeToValidate, LEAF_TYPE_CHILD_TYPES[allowedLeafType] ?? [], false) != null
      ) {
        return allowedLeafType;
      }
    }
  }

  return null;
}

/**
 * Validate that a leaf type is compatible with a list of allowed leaf types, including child leaf types.
 */
function validateLeafType(
  leafTypeToValidate: LeafType,
  allowedLeafTypes: ReadonlyArray<LeafType>,
  strictLeafType: boolean
): LeafType {
  const validatedLeafType = findAllowedLeafType(leafTypeToValidate, allowedLeafTypes, strictLeafType);
  if (validatedLeafType == null) {
    throw new ColumnTypeValidationException(
      `Leaf type ${leafTypeToValidate} does not match expected leaf types: ${allowedLeafTypes.join(', ')}`
    );
  }
  return validatedLeafType;
}

/**
 * Validate that a boolean flag from the column type is compatible with the corresponding flag from the
 * validation options.
 *
 * @param value The flag value from the column type
 * @param validationFlag The flag value from the validation options
 * @param name The name of the option to use in error message
 */
function validateFlag(
  value: true | undefined,
  validationFlag: ColumnTypeValidationFlag | undefined,
  name: string
): void {
  assert(value === undefined || value === true, 'Unexpected validation flag value', {
    validationFlagValue: value,
  });
  if (validationFlag === ColumnTypeValidationFlag.REQUIRED && value !== true) {
    throw new ColumnTypeValidationException(`Expected column to have a ${name} type.`);
  } else if (validationFlag === ColumnTypeValidationFlag.NOT_ALLOWED && value !== undefined) {
    throw new ColumnTypeValidationException(`Expected column to not have a ${name} type.`);
  }
}

/**
 * Validate that a data frame column type is expected, throwing an error if the column type is not valid.
 *
 * The validated leaf type will be returned.
 *
 * The validated leaf type will be one of the leaf types in `validationOptions.allowedLeafTypes`.
 *
 * If `columnType.leafType` if is not in `validationOptions.allowedLeafTypes` but its parent is,
 * the parent will be returned. See `LEAF_TYPE_CHILD_TYPES`.
 *
 * If `columnType.leafType` is the child of multiple types in `validationOptions.allowedLeafTypes`, the
 * first parent in `validationOptions.allowedLeafTypes` will be returned.
 *
 * Example usage:
 *
 *    ```
 *    const validatedLeafType = validateLeafType(
 *      {leafType: LeafType.BOX, isNullable: true},
 *      {
 *        allowedLeafTypes: [LeafType.STORABLE],
 *        listType: ColumnTypeValidationFlag.NOT_ALLOWED,
 *        maybeTextType?: ColumnTypeValidationFlag.NOT_ALLOWED;
 *        nullableType: ColumnTypeValidationFlag.ALLOWED,
 *      }
 *    );
 *    ```
 *
 * @param columnType The data frame column type to validate
 * @param validationOptions Options for validation
 * @return The validated leaf type
 * @throws ColumnTypeValidationException If the column type is invalid
 */
export default function validateColumnType(
  columnType: DataFrameColumnType,
  validationOptions: ColumnTypeValidationOptions
): LeafType {
  validateFlag(columnType.isList, validationOptions.listType, 'list');
  validateFlag(columnType.isNullable, validationOptions.nullableType, 'nullable');
  validateFlag(columnType.maybeText, validationOptions.maybeTextType, 'maybeText');
  return validationOptions.allowedLeafTypes
    ? validateLeafType(
        columnType.leafType,
        validationOptions.allowedLeafTypes,
        validationOptions.strictLeafType || false
      )
    : LeafType.JSON;
}

export function isValidColumnType(
  columnType: DataFrameColumnType,
  validationOptions: ColumnTypeValidationOptions
): boolean {
  try {
    validateColumnType(columnType, validationOptions);
    return true;
  } catch (err) {
    if (err instanceof ColumnTypeValidationException) {
      return false;
    }
    throw err;
  }
}
